import { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Headset, PaperPlaneRight, EnvelopeSimple, CircleNotch, CheckCircle, Paperclip } from "@phosphor-icons/react";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";

const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `s${Date.now()}`);

// Public customer-support widget: AI assistant + "contact the team" form. Used on Login and Pricing.
export default function SupportWidget({ triggerClassName = "", triggerLabel }) {
  const { tr } = useLang();
  const sid = useRef(uid());
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("chat"); // chat | contact | sent
  const [messages, setMessages] = useState([
    { role: "assistant", text: tr("Ciao! Sono l'assistente di Technical Hub. Come posso aiutarti?", "Hi! I'm the Technical Hub assistant. How can I help?") },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [contactError, setContactError] = useState("");
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => { if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" }); }, [messages, busy, mode]);

  const send = async () => {
    const q = input.trim();
    if (!q || busy) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setBusy(true);
    try {
      const r = await api.post("/support/ask", { session_id: sid.current, message: q });
      setMessages((m) => [...m, { role: "assistant", text: r.data.reply || tr("Non ho una risposta. Contatta il team qui sotto.", "I don't have an answer. Contact the team below.") }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: tr("Assistente non disponibile ora. Usa 'Contatta il team'.", "Assistant unavailable right now. Use 'Contact the team'.") }]);
    } finally { setBusy(false); }
  };

  const submitContact = async () => {
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!form.name.trim() || !form.message.trim()) { setContactError(tr("Compila nome e messaggio.", "Fill in name and message.")); return; }
    if (!emailOk) { setContactError(tr("Inserisci un'email valida.", "Enter a valid email.")); return; }
    if (busy) return;
    setContactError("");
    setBusy(true);
    try {
      let attachment_id = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/support/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        attachment_id = up.data.id;
      }
      const transcript = messages.map((m) => `${m.role === "user" ? "Utente" : "Assistente"}: ${m.text}`).join("\n");
      await api.post("/support/contact", { ...form, transcript, attachment_id });
      setMode("sent");
    } catch (e) {
      setContactError(e?.response?.status === 429 ? tr("Troppe richieste, riprova più tardi.", "Too many requests, try later.") : (e?.response?.data?.detail || tr("Invio non riuscito, riprova.", "Sending failed, please try again.")));
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="support-open-btn"
        className={`inline-flex items-center gap-2 text-sm font-semibold transition-colors ${triggerClassName}`}
      >
        <Headset size={18} weight="bold" /> {triggerLabel || tr("Serve aiuto?", "Need help?")}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="support-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 bg-primary text-primary-foreground" style={{ borderBottom: "3px solid hsl(var(--club-2, var(--primary)))" }}>
            <DialogTitle className="font-display uppercase text-sm flex items-center gap-2"><Headset size={18} weight="fill" /> {tr("Assistenza Technical Hub", "Technical Hub Support")}</DialogTitle>
            <DialogDescription className="text-primary-foreground/80 text-xs">{tr("Chiedi all'assistente o scrivi al nostro team.", "Ask the assistant or write to our team.")}</DialogDescription>
          </DialogHeader>

          {mode === "sent" ? (
            <div className="p-8 text-center space-y-3" data-testid="support-sent">
              <CheckCircle size={44} weight="fill" className="text-green-500 mx-auto" />
              <p className="font-display font-bold text-base">{tr("Richiesta inviata!", "Request sent!")}</p>
              <p className="text-sm text-muted-foreground">{tr("Ti risponderemo via email al più presto.", "We'll reply by email as soon as possible.")}</p>
              <Button size="sm" onClick={() => setOpen(false)} data-testid="support-close" className="mt-2">{tr("Chiudi", "Close")}</Button>
            </div>
          ) : mode === "contact" ? (
            <div className="p-5 space-y-3" data-testid="support-contact-form">
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder={tr("Il tuo nome", "Your name")} data-testid="support-name" className="h-9" />
              <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder={tr("La tua email", "Your email")} data-testid="support-email" className="h-9" />
              <Textarea value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} rows={4} placeholder={tr("Come possiamo aiutarti?", "How can we help?")} data-testid="support-message" />
              {contactError && <p className="text-xs text-red-500" data-testid="support-contact-error">{contactError}</p>}
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="support-file-input" />
                <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="support-attach-btn" className="gap-1.5 text-xs h-8"><Paperclip size={14} weight="bold" /> {tr("Allega schermata", "Attach screenshot")}</Button>
                {file && <span className="text-xs text-muted-foreground truncate flex items-center gap-1" data-testid="support-file-name">{file.name}<button onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-red-400 ml-1">✕</button></span>}
              </div>
              <div className="flex items-center justify-between gap-2">
                <button onClick={() => setMode("chat")} className="text-xs text-muted-foreground hover:text-foreground" data-testid="support-back-chat">{tr("← Torna all'assistente", "← Back to assistant")}</button>
                <Button size="sm" onClick={submitContact} disabled={busy} data-testid="support-submit" className="gap-1.5 font-display uppercase text-xs">
                  {busy ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneRight size={14} weight="bold" />} {tr("Invia", "Send")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col" data-testid="support-chat">
              <div className="px-5 py-3 space-y-3 max-h-[320px] overflow-y-auto" data-testid="support-messages">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`} data-testid={`support-msg-${m.role}`}>{m.text}</div>
                  </div>
                ))}
                {busy && <div className="flex justify-start"><div className="bg-muted rounded-lg px-3 py-2"><CircleNotch size={16} className="animate-spin text-muted-foreground" /></div></div>}
                <div ref={endRef} />
              </div>
              <div className="px-5 pb-3 pt-1 flex items-center gap-2 border-t border-border">
                <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={tr("Scrivi una domanda…", "Type a question…")} data-testid="support-input" className="h-9" />
                <Button size="icon" onClick={send} disabled={busy} data-testid="support-send" className="h-9 w-9 shrink-0"><PaperPlaneRight size={16} weight="bold" /></Button>
              </div>
              <button onClick={() => setMode("contact")} data-testid="support-to-contact" className="flex items-center justify-center gap-1.5 text-xs font-semibold text-primary hover:underline py-3 border-t border-border">
                <EnvelopeSimple size={14} weight="bold" /> {tr("Non hai trovato risposta? Contatta il team", "Didn't find an answer? Contact the team")}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
